import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { ChevronDown, Phone, Mail, HelpCircle } from 'lucide-react';

// FAQ data structure with categories
const faqData = {
  general: {
    en: {
      title: "General",
      questions: [
        {
          q: "How do I request a quote?",
          a: "You can request a quote by calling us at 469-434-8287, emailing info@palerminsurance.com, or using the quote request forms on our product pages. Our agents will guide you through the process in English or Spanish."
        },
        {
          q: "Do you provide service in Spanish?",
          a: "Yes, we are rooted in our Hispanic heritage and provide full service in Spanish. Our bilingual team is ready to assist you in the language you prefer."
        },
        {
          q: "In which states are you licensed?",
          a: "Please contact our agency at 469-434-8287 to confirm availability in your specific state. We cannot guarantee service in all states without verification."
        }
      ]
    },
    es: {
      title: "General",
      questions: [
        {
          q: "¿Cómo solicito una cotización?",
          a: "Puede solicitar una cotización llamándonos al 469-434-8287, enviando un correo a info@palerminsurance.com, o usando los formularios de solicitud en nuestras páginas de productos. Nuestros agentes lo guiarán en inglés o español."
        },
        {
          q: "¿Atienden en español?",
          a: "Sí, estamos arraigados en nuestra herencia hispana y brindamos servicio completo en español. Nuestro equipo bilingüe está listo para ayudarle en el idioma que prefiera."
        },
        {
          q: "¿En qué estados están licenciados?",
          a: "Por favor contacte a nuestra agencia al 469-434-8287 para confirmar disponibilidad en su estado específico. No podemos garantizar servicio en todos los estados sin verificación."
        }
      ]
    }
  },
  autoHome: {
    en: {
      title: "Auto & Home Insurance",
      questions: [
        {
          q: "What information do I need to get an auto insurance quote?",
          a: "You'll need: driver's license numbers, vehicle VIN(s), current coverage details (if applicable), driving history, and information about all drivers in your household."
        },
        {
          q: "When does my coverage start?",
          a: "Coverage starts only after policy issuance is confirmed and the effective date is established. Your agent will provide you with the specific effective date and policy documents."
        },
        {
          q: "Can I add or remove vehicles or drivers from my policy?",
          a: "Yes, but any changes to vehicles or drivers require review and confirmation by the insurance carrier. Contact your agent to process changes—they are not automatic."
        },
        {
          q: "What information is needed for a home insurance quote?",
          a: "You'll need: property address, year built, square footage, construction type, roofing material, coverage amounts desired, and mortgage information if applicable."
        },
        {
          q: "Do I need to inform if I work with Uber, Lyft, Uber Eats, DoorDash, or other platforms?",
          a: "Yes, you must inform your insurance agent before starting any rideshare or delivery activity. Using your personal vehicle for commercial purposes typically requires different coverage. Failing to disclose this use can result in claim denials. Contact your agent or insurance company to discuss your specific situation and coverage options—we cannot guarantee coverage without reviewing your policy and speaking with your insurer."
        },
        {
          q: "Does my personal auto insurance cover rideshare or delivery?",
          a: "Many personal auto policies exclude commercial use, including rideshare and delivery services. Coverage depends on your specific policy terms and your insurance company's guidelines. Some insurers offer endorsements or separate policies for these activities. Contact your agent or insurance company directly to understand your coverage and any gaps that may exist. Do not assume you are covered."
        },
        {
          q: "Does the platform's insurance replace my personal policy?",
          a: "Platform insurance typically provides coverage only during specific periods: while transporting a passenger or delivering an order. Coverage may vary or be unavailable when the app is off, waiting for requests, or en route to pick up. The interaction between your personal policy and platform coverage is complex and varies by insurer. Consult with your insurance agent and review both your personal policy and the platform's insurance details to understand when and how you're protected."
        },
        {
          q: "What insurance do I need for taxi or private passenger transport?",
          a: "Commercial passenger transport requires commercial auto insurance, which differs significantly from personal auto coverage. Contact your insurance agent to discuss the type of service, vehicle use, and operating details so we can connect you with appropriate commercial coverage options. Personal auto policies typically do not cover taxi or for-hire passenger transport."
        },
        {
          q: "What is the difference between liability, collision, and comprehensive coverage?",
          a: "Liability insurance protects you when you're at fault and cause bodily injury or property damage to others—it covers their medical expenses, lost wages, and property repairs. Collision coverage pays for physical damage to your vehicle when it collides with another vehicle or object, like a tree. Comprehensive coverage protects your vehicle from almost all other causes of damage, including fire, theft, severe weather, vandalism, and glass breakage. State law typically requires liability coverage, while collision and comprehensive are optional (though often required by lenders). Source: NAIC (https://content.naic.org/consumer/auto-insurance.htm)"
        },
        {
          q: "What is a deductible and how does it work?",
          a: "A deductible is the dollar amount you pay out-of-pocket before your insurance coverage kicks in for a claim. For example, if you have a $500 deductible and $2,000 in covered damage, you pay $500 and your insurer pays $1,500. Higher deductibles typically lower your premium cost, while lower deductibles mean higher premiums but less out-of-pocket expense when filing a claim. Deductibles commonly apply to collision and comprehensive coverages."
        },
        {
          q: "What factors affect my auto insurance premium?",
          a: "Several factors influence your rate: your driving record (violations and accidents increase risk), geographic location (urban areas typically have higher rates), age and gender (certain groups have higher claim frequency), marital status, prior insurance coverage history, annual mileage and vehicle use, and your vehicle's make and model (luxury and sports cars often cost more to insure). Each insurance company uses its own rating system, though claim frequency within similar risk groups is a major factor. Discounts may be available for multiple vehicles, driver education, good students, safety features, anti-theft devices, and low mileage."
        },
        {
          q: "What is uninsured/underinsured motorist coverage?",
          a: "Uninsured motorist coverage protects you and your passengers if you're injured by a driver who has no insurance or by a hit-and-run driver. Underinsured motorist coverage pays when the at-fault driver's insurance is insufficient to cover your damages. This coverage ensures you're protected even when others fail to carry adequate insurance. Coverage requirements vary by state—check with your agent or state insurance department for specific requirements in your area."
        },
        {
          q: "What should I look for when comparing auto insurance policies?",
          a: "When comparing policies, review: (1) coverage types and limits for liability, collision, comprehensive, and uninsured/underinsured motorist; (2) deductible amounts and how they affect your premium; (3) available discounts (multi-vehicle, good driver, safety features, etc.); (4) optional coverages like medical payments, rental reimbursement, and towing; (5) exclusions and limitations specific to each policy; (6) the insurer's claim service reputation and financial stability; and (7) total cost for comparable coverage levels. When requesting quotes, provide identical information to each agent or company to ensure accurate comparisons."
        },
        {
          q: "Can I lend my car to another person?",
          a: "This depends on permission granted, excluded drivers listed in your policy, and specific policy terms. Not all situations are covered—review your policy or contact your agent before lending your vehicle."
        },
        {
          q: "Does my auto insurance cover rental car reimbursement?",
          a: "Only if you have applicable rental reimbursement coverage on your policy. This coverage has daily and total limits. Contact your agent to verify if you have this coverage and what the limits are."
        },
        {
          q: "What is GAP insurance?",
          a: "GAP (Guaranteed Asset Protection) may cover the difference between your vehicle's value and your loan balance if totaled. Coverage is subject to policy terms and conditions—not all situations qualify."
        },
        {
          q: "Does my auto insurance cover me in Mexico?",
          a: "Most U.S. auto policies do not provide coverage in Mexico. You must obtain appropriate Mexican insurance before traveling. Contact us to discuss options for Mexico coverage."
        },
        {
          q: "What does a percentage deductible mean for home insurance?",
          a: "A percentage deductible is based on your home's insured value as stated in the policy. For example (hypothetical): if your home is insured for $200,000 and has a 2% deductible, you would pay the first $4,000 of a covered claim. Learn more at TDI: https://tdi.texas.gov/tips/deductibles.html"
        },
        {
          q: "Does my homeowners insurance cover flooding?",
          a: "Flooding generally requires specific flood insurance—standard homeowners policies typically exclude flood damage. However, accidental water damage from burst pipes may be covered under your homeowners policy. For flood insurance information (Texas): https://www.opic.texas.gov/es/seguro-contra-inundaciones/conceptos-basicos/"
        },
        {
          q: "How do I know if my dwelling coverage limit is sufficient?",
          a: "Your dwelling coverage should reflect the cost to rebuild your home at current construction prices—not market value. Review your policy annually and after renovations. Your agent can help assess if your coverage is adequate."
        }
      ]
    },
    es: {
      title: "Seguro de Auto y Hogar",
      questions: [
        {
          q: "¿Qué información necesito para cotizar seguro de auto?",
          a: "Necesitará: números de licencia de conducir, VIN(s) del vehículo, detalles de cobertura actual (si aplica), historial de manejo e información de todos los conductores en su hogar."
        },
        {
          q: "¿Cuándo comienza mi cobertura?",
          a: "La cobertura comienza solo después de confirmar la emisión de la póliza y establecer la fecha efectiva. Su agente le proporcionará la fecha efectiva específica y los documentos de póliza."
        },
        {
          q: "¿Puedo agregar o quitar vehículos o conductores de mi póliza?",
          a: "Sí, pero cualquier cambio de vehículos o conductores requiere revisión y confirmación por la aseguradora. Contacte a su agente para procesar cambios—no son automáticos."
        },
        {
          q: "¿Qué información se necesita para cotizar seguro de hogar?",
          a: "Necesitará: dirección de la propiedad, año de construcción, pies cuadrados, tipo de construcción, material del techo, montos de cobertura deseados e información de hipoteca si aplica."
        },
        {
          q: "¿Debo informar si trabajo con Uber, Lyft, Uber Eats, DoorDash u otra plataforma?",
          a: "Sí, debe informar a su agente de seguros antes de comenzar cualquier actividad de rideshare o delivery. Usar su vehículo personal para fines comerciales típicamente requiere cobertura diferente. No revelar este uso puede resultar en la denegación de reclamos. Contacte a su agente o compañía de seguros para discutir su situación específica y opciones de cobertura—no podemos garantizar cobertura sin revisar su póliza y hablar con su aseguradora."
        },
        {
          q: "¿Mi seguro de auto personal cubre rideshare o delivery?",
          a: "Muchas pólizas de auto personal excluyen uso comercial, incluyendo servicios de rideshare y delivery. La cobertura depende de los términos específicos de su póliza y las directrices de su compañía de seguros. Algunas aseguradoras ofrecen endosos o pólizas separadas para estas actividades. Contacte a su agente o compañía de seguros directamente para entender su cobertura y cualquier brecha que pueda existir. No asuma que está cubierto."
        },
        {
          q: "¿El seguro de la plataforma sustituye mi póliza personal?",
          a: "El seguro de la plataforma típicamente proporciona cobertura solo durante períodos específicos: mientras transporta un pasajero o entrega un pedido. La cobertura puede variar o no estar disponible cuando la app está apagada, esperando solicitudes, o en camino a recoger. La interacción entre su póliza personal y la cobertura de la plataforma es compleja y varía por aseguradora. Consulte con su agente de seguros y revise tanto su póliza personal como los detalles del seguro de la plataforma para entender cuándo y cómo está protegido."
        },
        {
          q: "¿Qué seguro necesito para taxi o transporte privado de pasajeros?",
          a: "El transporte comercial de pasajeros requiere seguro de auto comercial, que difiere significativamente de la cobertura de auto personal. Contacte a su agente de seguros para discutir el tipo de servicio, uso del vehículo y detalles de operación para que podamos conectarlo con opciones de cobertura comercial apropiadas. Las pólizas de auto personal típicamente no cubren taxi o transporte de pasajeros por contratación."
        },
        {
          q: "¿Cuál es la diferencia entre responsabilidad civil, colisión y cobertura comprensiva?",
          a: "El seguro de responsabilidad civil lo protege cuando usted es culpable y causa lesiones corporales o daños a la propiedad de otros—cubre sus gastos médicos, salarios perdidos y reparaciones de propiedad. La cobertura de colisión paga por daños físicos a su vehículo cuando colisiona con otro vehículo u objeto, como un árbol. La cobertura comprensiva protege su vehículo de casi todas las demás causas de daño, incluyendo incendio, robo, clima severo, vandalismo y rotura de vidrios. La ley estatal generalmente requiere cobertura de responsabilidad civil, mientras que colisión y comprensiva son opcionales (aunque a menudo requeridas por prestamistas). Fuente: NAIC (https://content.naic.org/consumer/auto-insurance.htm)"
        },
        {
          q: "¿Qué es un deducible y cómo funciona?",
          a: "Un deducible es la cantidad en dólares que usted paga de su bolsillo antes de que su cobertura de seguro entre en vigor para un reclamo. Por ejemplo, si tiene un deducible de $500 y $2,000 en daños cubiertos, usted paga $500 y su aseguradora paga $1,500. Deducibles más altos típicamente reducen el costo de su prima, mientras que deducibles más bajos significan primas más altas pero menos gasto de bolsillo al presentar un reclamo. Los deducibles comúnmente aplican a coberturas de colisión y comprensiva."
        },
        {
          q: "¿Qué factores afectan mi prima de seguro de auto?",
          a: "Varios factores influyen en su tarifa: su historial de manejo (infracciones y accidentes aumentan el riesgo), ubicación geográfica (áreas urbanas típicamente tienen tarifas más altas), edad y género (ciertos grupos tienen mayor frecuencia de reclamos), estado civil, historial de cobertura de seguro previa, kilometraje anual y uso del vehículo, y la marca y modelo de su vehículo (autos de lujo y deportivos a menudo cuestan más asegurar). Cada compañía de seguros usa su propio sistema de tarifas, aunque la frecuencia de reclamos dentro de grupos de riesgo similares es un factor importante. Descuentos pueden estar disponibles por múltiples vehículos, educación vial, buenos estudiantes, características de seguridad, dispositivos antirrobo y bajo kilometraje."
        },
        {
          q: "¿Qué es la cobertura de motorista sin seguro/con seguro insuficiente?",
          a: "La cobertura de motorista sin seguro lo protege a usted y a sus pasajeros si resultan lesionados por un conductor que no tiene seguro o por un conductor que se da a la fuga. La cobertura de motorista con seguro insuficiente paga cuando el seguro del conductor culpable es insuficiente para cubrir sus daños. Esta cobertura asegura que esté protegido incluso cuando otros no tienen seguro adecuado. Los requisitos de cobertura varían por estado—consulte con su agente o departamento de seguros estatal para requisitos específicos en su área."
        },
        {
          q: "¿Qué debo buscar al comparar pólizas de seguro de auto?",
          a: "Al comparar pólizas, revise: (1) tipos de cobertura y límites para responsabilidad civil, colisión, comprensiva y motorista sin/con seguro insuficiente; (2) montos de deducibles y cómo afectan su prima; (3) descuentos disponibles (múltiples vehículos, buen conductor, características de seguridad, etc.); (4) coberturas opcionales como pagos médicos, reembolso de alquiler y remolque; (5) exclusiones y limitaciones específicas de cada póliza; (6) reputación del servicio de reclamos y estabilidad financiera de la aseguradora; y (7) costo total para niveles de cobertura comparables. Al solicitar cotizaciones, proporcione información idéntica a cada agente o compañía para asegurar comparaciones precisas."
        },
        {
          q: "¿Puedo prestar mi auto a otra persona?",
          a: "Esto depende del permiso otorgado, conductores excluidos listados en su póliza y términos específicos de la póliza. No todas las situaciones están cubiertas—revise su póliza o contacte a su agente antes de prestar su vehículo."
        },
        {
          q: "¿Mi seguro de auto cubre reembolso de alquiler de auto?",
          a: "Solo si tiene cobertura de reembolso de alquiler aplicable en su póliza. Esta cobertura tiene límites diarios y totales. Contacte a su agente para verificar si tiene esta cobertura y cuáles son los límites."
        },
        {
          q: "¿Qué es el seguro GAP?",
          a: "GAP (Protección de Activos Garantizada) puede cubrir la diferencia entre el valor de su vehículo y el saldo de su préstamo si es pérdida total. La cobertura está sujeta a términos y condiciones de la póliza—no todas las situaciones califican."
        },
        {
          q: "¿Mi seguro de auto me cubre en México?",
          a: "La mayoría de las pólizas de auto de EE.UU. no proporcionan cobertura en México. Debe obtener un seguro mexicano apropiado antes de viajar. Contáctenos para discutir opciones de cobertura para México."
        },
        {
          q: "¿Qué significa un deducible de porcentaje para el seguro de hogar?",
          a: "Un deducible de porcentaje se basa en el valor asegurado de su hogar indicado en la póliza. Por ejemplo (hipotético): si su hogar está asegurado por $200,000 y tiene un deducible del 2%, usted pagaría los primeros $4,000 de un reclamo cubierto. Más información en TDI: https://tdi.texas.gov/tips/deductibles.html"
        },
        {
          q: "¿Mi seguro de propietarios cubre inundaciones?",
          a: "Las inundaciones generalmente requieren seguro contra inundaciones específico—las pólizas estándar de propietarios típicamente excluyen daños por inundación. Sin embargo, el daño accidental por agua de tuberías rotas puede estar cubierto bajo su póliza de propietarios. Para información sobre seguro contra inundaciones (Texas): https://www.opic.texas.gov/es/seguro-contra-inundaciones/conceptos-basicos/"
        },
        {
          q: "¿Cómo sé si mi límite de cobertura de vivienda es suficiente?",
          a: "Su cobertura de vivienda debe reflejar el costo de reconstruir su hogar a precios actuales de construcción—no el valor de mercado. Revise su póliza anualmente y después de renovaciones. Su agente puede ayudarle a evaluar si su cobertura es adecuada."
        }
      ]
    }
  },
  commercial: {
    en: {
      title: "Commercial & Trucking Insurance",
      questions: [
        {
          q: "What information do I need to quote commercial trucking insurance?",
          a: "You'll need: USDOT/MC number (if applicable), type of operation (local/long haul), radius of operation, number and VIN of units, driver information, loss history, and any additional documents requested by the insurance carrier. Requirements vary by carrier and operation type."
        },
        {
          q: "What is the difference between commercial auto liability, physical damage, and motor truck cargo coverage?",
          a: "Commercial auto liability covers bodily injury and property damage to others. Physical damage covers your own vehicles (collision/comprehensive). Motor truck cargo covers goods being transported. Coverage availability and terms depend on your specific policy—we cannot promise automatic coverage for any of these without carrier approval."
        },
        {
          q: "How do I request a COI (Certificate of Insurance), Additional Insured, or Waiver?",
          a: "Contact your agent to request these documents. COI, Additional Insured endorsements, and Waivers are subject to policy terms and require carrier approval—they are not automatic. Processing time depends on the carrier."
        }
      ]
    },
    es: {
      title: "Seguro Comercial y de Camiones",
      questions: [
        {
          q: "¿Qué información necesito para cotizar seguro de camiones comerciales?",
          a: "Necesitará: número USDOT/MC (si aplica), tipo de operación (local/larga distancia), radio de operación, número y VIN de unidades, información de conductores, historial de pérdidas y documentos adicionales que solicite la aseguradora. Los requisitos varían según la aseguradora y tipo de operación."
        },
        {
          q: "¿Cuál es la diferencia entre responsabilidad de auto comercial, daño físico y carga de camión?",
          a: "La responsabilidad de auto comercial cubre lesiones corporales y daños a propiedad de terceros. Daño físico cubre sus propios vehículos (colisión/comprensiva). Carga de camión cubre mercancía transportada. Disponibilidad y términos dependen de su póliza específica—no podemos prometer cobertura automática sin aprobación de la aseguradora."
        },
        {
          q: "¿Cómo solicito un COI (Certificado de Seguro), Asegurado Adicional o Renuncia?",
          a: "Contacte a su agente para solicitar estos documentos. COI, endosos de Asegurado Adicional y Renuncias están sujetos a términos de póliza y requieren aprobación de la aseguradora—no son automáticos. El tiempo de procesamiento depende de la aseguradora."
        }
      ]
    }
  },
  consumerProtection: {
    en: {
      title: "Consumer Protection & Fraud Prevention",
      questions: [
        {
          q: "How can I verify if an insurance agent or company is legitimate?",
          a: "You can verify insurance agents, agencies, and companies through the Texas Department of Insurance at https://www.tdi.texas.gov/agent/ or through the National Insurance Producer Registry (NIPR) at https://nipr.com. Always verify credentials before purchasing insurance or providing personal information."
        },
        {
          q: "How do I file a complaint against an insurance company or agent?",
          a: "You can file a complaint with the Texas Department of Insurance at https://www.tdi.texas.gov/consumer/complfrm.html or by calling 1-800-252-3439. For fraud-related complaints, you can also contact the Texas Attorney General's Consumer Protection Division at https://www.texasattorneygeneral.gov/consumer-protection/file-consumer-complaint"
        }
      ]
    },
    es: {
      title: "Protección al Consumidor y Prevención de Fraude",
      questions: [
        {
          q: "¿Cómo puedo verificar si un agente o compañía de seguros es legítimo?",
          a: "Puede verificar agentes, agencias y compañías de seguros a través del Departamento de Seguros de Texas en https://www.tdi.texas.gov/agent/ o a través del Registro Nacional de Productores de Seguros (NIPR) en https://nipr.com. Siempre verifique las credenciales antes de comprar seguros o proporcionar información personal."
        },
        {
          q: "¿Cómo presento una queja contra una compañía o agente de seguros?",
          a: "Puede presentar una queja con el Departamento de Seguros de Texas en https://www.tdi.texas.gov/consumer/complfrm.html o llamando al 1-800-252-3439. Para quejas relacionadas con fraude, también puede contactar a la División de Protección al Consumidor de la Procuraduría General de Texas en https://www.texasattorneygeneral.gov/consumer-protection/file-consumer-complaint"
        }
      ]
    }
  },
  payments: {
    en: {
      title: "Payments & Service",
      questions: [
        {
          q: "How do I make a payment?",
          a: "Use the official payment portal of your insurance carrier. Your agent can provide you with the correct link or payment information. We cannot promise payment extensions or grace periods—those are determined by your insurance carrier."
        },
        {
          q: "How do I request ID cards and policy documents?",
          a: "Contact your agent at 469-434-8287 or info@palerminsurance.com to request ID cards or policy documents. Processing time varies by carrier."
        },
        {
          q: "How do I report a claim?",
          a: "For emergencies, call 911 first. Then, report the claim directly to your insurance carrier using the claims number on your policy documents. Contact our agency for guidance and support throughout the claims process."
        }
      ]
    },
    es: {
      title: "Pagos y Servicio",
      questions: [
        {
          q: "¿Cómo hago un pago?",
          a: "Use el portal oficial de pagos de su aseguradora. Su agente puede proporcionarle el enlace correcto o información de pago. No podemos prometer extensiones de pago o períodos de gracia—esos son determinados por su aseguradora."
        },
        {
          q: "¿Cómo solicito tarjetas de identificación y documentos de póliza?",
          a: "Contacte a su agente al 469-434-8287 o info@palerminsurance.com para solicitar tarjetas de ID o documentos de póliza. El tiempo de procesamiento varía según la aseguradora."
        },
        {
          q: "¿Cómo reporto una reclamación?",
          a: "Para emergencias, llame al 911 primero. Luego, reporte la reclamación directamente a su aseguradora usando el número de reclamos en sus documentos de póliza. Contacte a nuestra agencia para orientación y apoyo durante el proceso de reclamación."
        }
      ]
    }
  }
};

const FAQAccordion = ({ question, answer, isOpen, onToggle }) => {
  // Function to render answer with links
  const renderAnswer = (text) => {
    // Check if text contains TDI, OPIC, or Texas Attorney General links
    const linkPattern = /(https:\/\/(?:www\.)?(?:tdi\.texas\.gov|opic\.texas\.gov|texasattorneygeneral\.gov|nipr\.com)[^\s)]+)/g;
    const parts = text.split(linkPattern);
    
    return parts.map((part, index) => {
      if (part.match(linkPattern)) {
        return (
          <a
            key={index}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800 underline"
          >
            {part}
          </a>
        );
      }
      return <span key={index}>{part}</span>;
    });
  };

  return (
    <div className="border-b border-gray-200 last:border-0">
      <button
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
        className="w-full flex justify-between items-center py-5 px-6 text-left hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset"
        aria-expanded={isOpen}
      >
        <span className="font-semibold text-gray-900 pr-4">{question}</span>
        <ChevronDown 
          className={`w-5 h-5 text-blue-600 flex-shrink-0 transition-transform duration-200 ${
            isOpen ? 'transform rotate-180' : ''
          }`}
        />
      </button>
      {isOpen && (
        <div className="px-6 pb-5 text-gray-700 leading-relaxed">
          {renderAnswer(answer)}
        </div>
      )}
    </div>
  );
};

const FAQPage = () => {
  const { language } = useLanguage();
  const [openItems, setOpenItems] = useState({});

  const toggleItem = (category, index) => {
    const key = `${category}-${index}`;
    setOpenItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const content = {
    en: {
      title: "Frequently Asked Questions",
      subtitle: "Find answers to common questions about our insurance services",
      disclaimer: "Note: These are general answers. The specific terms and conditions of your policy govern coverage.",
      notFound: "Can't find your answer?",
      contactUs: "Contact us and we'll be happy to help:",
      phone: "Phone",
      email: "Email"
    },
    es: {
      title: "Preguntas Frecuentes",
      subtitle: "Encuentre respuestas a preguntas comunes sobre nuestros servicios de seguros",
      disclaimer: "Nota: Estas son respuestas generales. Los términos y condiciones específicos de su póliza rigen la cobertura.",
      notFound: "¿No encuentras tu respuesta?",
      contactUs: "Contáctanos y con gusto te ayudaremos:",
      phone: "Teléfono",
      email: "Correo"
    }
  };

  const text = content[language];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-700 to-blue-900 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block bg-white/10 p-4 rounded-full mb-6">
              <HelpCircle className="w-12 h-12" />
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              {text.title}
            </h1>
            <p className="text-xl md:text-2xl text-blue-100">
              {text.subtitle}
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Disclaimer */}
            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-lg mb-12">
              <p className="text-sm text-blue-900">
                <strong>ℹ️ {language === 'en' ? 'Important:' : 'Importante:'}</strong> {text.disclaimer}
              </p>
            </div>

            {/* FAQ Categories */}
            <div className="space-y-8">
              {/* General */}
              <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
                  <h2 className="text-2xl font-bold text-white">
                    {faqData.general[language].title}
                  </h2>
                </div>
                <div>
                  {faqData.general[language].questions.map((item, index) => (
                    <FAQAccordion
                      key={index}
                      question={item.q}
                      answer={item.a}
                      isOpen={openItems[`general-${index}`]}
                      onToggle={() => toggleItem('general', index)}
                    />
                  ))}
                </div>
              </div>

              {/* Auto & Home */}
              <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className="bg-gradient-to-r from-green-600 to-green-700 px-6 py-4">
                  <h2 className="text-2xl font-bold text-white">
                    {faqData.autoHome[language].title}
                  </h2>
                </div>
                <div>
                  {faqData.autoHome[language].questions.map((item, index) => (
                    <FAQAccordion
                      key={index}
                      question={item.q}
                      answer={item.a}
                      isOpen={openItems[`autoHome-${index}`]}
                      onToggle={() => toggleItem('autoHome', index)}
                    />
                  ))}
                </div>
              </div>

              {/* Commercial & Trucking */}
              <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className="bg-gradient-to-r from-orange-600 to-orange-700 px-6 py-4">
                  <h2 className="text-2xl font-bold text-white">
                    {faqData.commercial[language].title}
                  </h2>
                </div>
                <div>
                  {faqData.commercial[language].questions.map((item, index) => (
                    <FAQAccordion
                      key={index}
                      question={item.q}
                      answer={item.a}
                      isOpen={openItems[`commercial-${index}`]}
                      onToggle={() => toggleItem('commercial', index)}
                    />
                  ))}
                </div>
              </div>

              {/* Consumer Protection & Fraud Prevention */}
              <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className="bg-gradient-to-r from-red-600 to-red-700 px-6 py-4">
                  <h2 className="text-2xl font-bold text-white">
                    {faqData.consumerProtection[language].title}
                  </h2>
                </div>
                <div>
                  {faqData.consumerProtection[language].questions.map((item, index) => (
                    <FAQAccordion
                      key={index}
                      question={item.q}
                      answer={item.a}
                      isOpen={openItems[`consumerProtection-${index}`]}
                      onToggle={() => toggleItem('consumerProtection', index)}
                    />
                  ))}
                </div>
              </div>

              {/* Payments & Service */}
              <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-6 py-4">
                  <h2 className="text-2xl font-bold text-white">
                    {faqData.payments[language].title}
                  </h2>
                </div>
                <div>
                  {faqData.payments[language].questions.map((item, index) => (
                    <FAQAccordion
                      key={index}
                      question={item.q}
                      answer={item.a}
                      isOpen={openItems[`payments-${index}`]}
                      onToggle={() => toggleItem('payments', index)}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-12 bg-gradient-to-r from-blue-600 to-blue-700">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center text-white">
            <h2 className="text-3xl font-bold mb-4">{text.notFound}</h2>
            <p className="text-xl mb-8 text-blue-100">{text.contactUs}</p>
            
            <div className="flex flex-col md:flex-row justify-center gap-6">
              <a
                href="tel:469-434-8287"
                className="inline-flex items-center justify-center gap-3 bg-white text-blue-700 px-8 py-4 rounded-lg font-bold text-lg hover:bg-blue-50 transition-colors shadow-lg"
              >
                <Phone className="w-6 h-6" />
                <div className="text-left">
                  <div className="text-sm font-normal text-blue-600">{text.phone}</div>
                  <div>469-434-8287</div>
                </div>
              </a>
              
              <a
                href="mailto:info@palerminsurance.com"
                className="inline-flex items-center justify-center gap-3 bg-white text-blue-700 px-8 py-4 rounded-lg font-bold text-lg hover:bg-blue-50 transition-colors shadow-lg"
              >
                <Mail className="w-6 h-6" />
                <div className="text-left">
                  <div className="text-sm font-normal text-blue-600">{text.email}</div>
                  <div>info@palerminsurance.com</div>
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQPage;
